import { action, observable } from 'mobx';

import { apiAuthCaller } from '@services/apiCaller';
import { IRootStore } from '@stores/index';
import { getApiErrorMessage } from '@components/common/util/util';

import { AudienceStore } from './audience';
import { IAudience, FilterSpecProps, IAudienceListStore, CreatePayload } from './types';

export class AudienceListStore implements IAudienceListStore {
  rootStore: IRootStore;

  @observable audiences: AudienceStore[] = [];

  @observable firstLoad = false;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action.bound
  setAudiences(audiences: AudienceStore[]) {
    this.audiences = audiences;
  }

  @action.bound
  async getAudiences() {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;

    try {
      const {
        data: { audiences },
      } = await apiAuthCaller(token).get<{ audiences: IAudience[] }>('/audiences');
      const audienceStores = audiences.map(
        (audience) => new AudienceStore(audience, this.rootStore),
      );

      this.setAudiences(audienceStores);
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to get audiences'));
    } finally {
      this.firstLoad = true;
    }
  }

  @action.bound
  async createAudience(audience: CreatePayload): Promise<IAudience> {
    const {
      userStore: { token },
    } = this.rootStore;

    const response = await apiAuthCaller(token).post<IAudience>('/audiences', audience);

    const savedAudience = new AudienceStore(response.data, this.rootStore);

    this.setAudiences([savedAudience, ...this.audiences]);

    return savedAudience;
  }

  @action.bound
  public getAudienceById = (id: string) => this.audiences.find((audience) => audience.id === id);

  @action.bound
  async deleteAudience(id: string) {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;

    try {
      const response = await apiAuthCaller(token).delete<IAudience>(`/audiences/${id}`);
      this.setAudiences(this.audiences.filter((audience) => audience.id !== response.data.id));
      return true;
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to delete audience'));
      return false;
    }
  }

  @action.bound
  async generateSqlQuery(filterSpec: FilterSpecProps) {
    const {
      userStore: { token },
    } = this.rootStore;
    const response = await apiAuthCaller(token).post<{ sql: string }>(
      `/cloudSources/audience/query-builder/query`,
      filterSpec,
    );
    return response.data.sql;
  }
}
